import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, ArrowLeft, Star, User, Euro, Check, X, RefreshCw, Languages } from 'lucide-react';
import { messagesAPI, photoAPI, offersAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

function timeAgo(dateStr, t, locale) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return t('msg.now');
  if (diff < 3600) return t('msg.min_ago', { n: Math.floor(diff / 60) });
  if (diff < 86400) return t('msg.h_ago', { n: Math.floor(diff / 3600) });
  if (diff < 604800) return t('msg.d_ago', { n: Math.floor(diff / 86400) });
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

function formatDate(dateStr, locale) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

function memberSince(dateStr, locale) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

// Group messages by date for separators
function groupMessagesByDate(messages) {
  const groups = [];
  let currentDate = null;
  for (const msg of messages) {
    const msgDate = new Date(msg.created_at).toDateString();
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groups.push({ type: 'date', date: msg.created_at });
    }
    groups.push({ type: 'message', ...msg });
  }
  return groups;
}

export default function MessagesTab() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const loc = i18n.language === 'en' ? 'en-GB' : `${i18n.language}-${i18n.language.toUpperCase()}`;
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeOffer, setActiveOffer] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [isSeller, setIsSeller] = useState(false);
  const [newMsg, setNewMsg] = useState('');
  const [showOriginal, setShowOriginal] = useState({});
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [offerLoading, setOfferLoading] = useState(false);
  const [showMakeOffer, setShowMakeOffer] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [counterAmount, setCounterAmount] = useState('');
  const [showCounter, setShowCounter] = useState(false);
  const messagesEnd = useRef(null);
  const messagesContainer = useRef(null);
  const pollRef = useRef(null);
  const lastMessageCount = useRef(0);
  const isInitialLoad = useRef(true);

  const loadConversations = useCallback(() => {
    messagesAPI.getConversations().then(r => {
      setConversations(r.data.conversations || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const openConversation = async (conv) => {
    setActiveConv(conv);
    setActiveOffer(null);
    setOtherUser(null);
    isInitialLoad.current = true;
    try {
      const r = await messagesAPI.getMessages(conv.id, i18n.language);
      setMessages(r.data.messages || []);
      lastMessageCount.current = (r.data.messages || []).length;
      setActiveOffer(r.data.active_offer || null);
      setOtherUser(r.data.other_user || null);
      setIsSeller(r.data.is_seller || false);
      loadConversations();
    } catch { toast.error(t('msg.error_loading')); }
  };

  // Poll for new messages when conversation is open
  useEffect(() => {
    if (!activeConv) { clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      try {
        const r = await messagesAPI.getMessages(activeConv.id, i18n.language);
        const newMessages = r.data.messages || [];
        // Only update if new messages arrived
        if (newMessages.length !== lastMessageCount.current) {
          setMessages(newMessages);
          lastMessageCount.current = newMessages.length;
        }
        setActiveOffer(r.data.active_offer || null);
      } catch {}
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeConv]);

  // Scroll to bottom only on initial load or when sending a new message
  useEffect(() => {
    if (isInitialLoad.current && messages.length > 0) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        messagesEnd.current?.scrollIntoView({ behavior: 'auto' });
        isInitialLoad.current = false;
      }, 100);
    }
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await messagesAPI.sendMessage(activeConv.id, newMsg.trim(), i18n.language);
      setNewMsg('');
      const r = await messagesAPI.getMessages(activeConv.id, i18n.language);
      setMessages(r.data.messages || []);
      lastMessageCount.current = (r.data.messages || []).length;
      setActiveOffer(r.data.active_offer || null);
      loadConversations();
      // Scroll to bottom after sending
      setTimeout(() => {
        messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('msg.error_send'));
    } finally { setSending(false); }
  };

  // Offer actions
  const handleAcceptOffer = async () => {
    if (!activeOffer) return;
    setOfferLoading(true);
    try {
      if (activeOffer.status === 'countered') {
        await offersAPI.acceptCounter(activeOffer.id);
      } else {
        await offersAPI.accept(activeOffer.id);
      }
      toast.success(t('msg.offer_accepted'));
      const r = await messagesAPI.getMessages(activeConv.id, i18n.language);
      setMessages(r.data.messages || []);
      setActiveOffer(r.data.active_offer || null);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('msg.error'));
    } finally { setOfferLoading(false); }
  };

  const handleDeclineOffer = async () => {
    if (!activeOffer) return;
    setOfferLoading(true);
    try {
      await offersAPI.decline(activeOffer.id);
      toast.success(t('msg.offer_declined'));
      const r = await messagesAPI.getMessages(activeConv.id, i18n.language);
      setMessages(r.data.messages || []);
      setActiveOffer(r.data.active_offer || null);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('msg.error'));
    } finally { setOfferLoading(false); }
  };

  const handleCounterOffer = async () => {
    if (!activeOffer || !counterAmount) return;
    const amount = parseFloat(counterAmount);
    if (isNaN(amount) || amount <= activeOffer.amount || amount >= activeOffer.original_price) {
      toast.error(t('msg.invalid_amount'));
      return;
    }
    setOfferLoading(true);
    try {
      await offersAPI.counter(activeOffer.id, amount);
      toast.success(t('msg.counter_sent'));
      setShowCounter(false);
      setCounterAmount('');
      const r = await messagesAPI.getMessages(activeConv.id, i18n.language);
      setMessages(r.data.messages || []);
      setActiveOffer(r.data.active_offer || null);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('msg.error'));
    } finally { setOfferLoading(false); }
  };

  const handleMakeOffer = async () => {
    if (!offerAmount || !activeConv) return;
    const amount = parseFloat(offerAmount);
    const listingPrice = activeConv.listing_snapshot?.price || 0;
    if (isNaN(amount) || amount <= 0 || amount >= listingPrice) {
      toast.error(t('msg.amount_below_price'));
      return;
    }
    setOfferLoading(true);
    try {
      await offersAPI.create({ listing_id: activeConv.listing_id, amount });
      toast.success(t('msg.offer_sent'));
      setShowMakeOffer(false);
      setOfferAmount('');
      const r = await messagesAPI.getMessages(activeConv.id, i18n.language);
      setMessages(r.data.messages || []);
      setActiveOffer(r.data.active_offer || null);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('msg.error'));
    } finally { setOfferLoading(false); }
  };

  if (loading) return <div className="text-center text-sm text-[#5A8BA8] py-12">{t('msg.loading')}</div>;

  // Conversation list view
  if (!activeConv) {
    if (conversations.length === 0) {
      return (
        <div className="text-center py-12" data-testid="no-messages">
          <MessageCircle className="w-12 h-12 text-[#D6E8F0] mx-auto mb-3" />
          <p className="text-[#5A8BA8] font-medium">{t('msg.no_conversations')}</p>
          <p className="text-xs text-[#B8D4E3] mt-1">{t('msg.no_conversations_hint')}</p>
        </div>
      );
    }
    return (
      <div className="space-y-2" data-testid="conversations-list">
        {conversations.map(c => (
          <button key={c.id} onClick={() => openConversation(c)}
            className={`w-full text-left p-3 rounded-xl border transition-colors hover:bg-[#F0F7FA] flex items-center gap-3 ${c.unread > 0 ? 'border-[#0C63BF] bg-blue-50/50' : 'border-[#E8F4FD] bg-white'}`}
            data-testid={`conversation-${c.id}`}>
            {c.listing_snapshot?.photo ? (
              <img src={photoAPI.getUrl(c.listing_snapshot.photo)} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-[#F0F7FA] flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-[#B8D4E3]" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className={`text-sm truncate ${c.unread > 0 ? 'font-bold text-[#033871]' : 'font-medium text-[#033871]'}`}>
                  {c.other_name}
                </p>
                <span className="text-[10px] text-[#B8D4E3] shrink-0">{timeAgo(c.last_message_at, t, loc)}</span>
              </div>
              <p className="text-xs text-[#5A8BA8] truncate">{c.listing_snapshot?.brand} {c.listing_snapshot?.model}</p>
              <p className={`text-xs truncate mt-0.5 ${c.unread > 0 ? 'text-[#033871] font-medium' : 'text-[#B8D4E3]'}`}>
                {c.last_message || t('msg.no_messages_yet')}
              </p>
            </div>
            {c.unread > 0 && (
              <span className="w-5 h-5 rounded-full bg-[#0C63BF] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                {c.unread}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  const groupedMessages = groupMessagesByDate(messages);
  const listingSnap = activeConv.listing_snapshot || {};
  
  // Determine offer display
  const showOfferActions = activeOffer && (
    (isSeller && activeOffer.status === 'pending') ||
    (!isSeller && activeOffer.status === 'countered')
  );
  const canMakeOffer = !isSeller && !activeOffer;
  const offerDisplayAmount = activeOffer?.status === 'countered' ? activeOffer.counter_amount : activeOffer?.amount;

  // Active conversation view - PadSmash style (mobile responsive)
  return (
    <div className="flex flex-col h-[calc(100vh-200px)] min-h-[400px] max-h-[600px] md:h-[550px] bg-white rounded-xl border border-[#E8F4FD] overflow-hidden" data-testid="active-conversation">
      {/* Header with avatar and user info */}
      <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border-b border-[#E8F4FD] shrink-0">
        <button onClick={() => setActiveConv(null)} className="p-1 sm:p-1.5 rounded-lg hover:bg-[#F0F7FA]" data-testid="back-to-conversations">
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-[#5A8BA8]" />
        </button>
        
        {/* Avatar */}
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#0C63BF] flex items-center justify-center text-white font-bold text-sm sm:text-lg shrink-0">
          {(otherUser?.first_name || activeConv.other_name || 'U')[0].toUpperCase()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="font-semibold text-[#033871] text-sm sm:text-base truncate">{otherUser?.first_name || activeConv.other_name}</span>
            {otherUser?.avg_rating && (
              <span className="flex items-center gap-0.5 text-xs sm:text-sm shrink-0">
                <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-[#32CD32] text-[#32CD32]" />
                <span className="font-medium text-[#033871]">{otherUser.avg_rating.toFixed(1)}</span>
              </span>
            )}
          </div>
          <p className="text-[10px] sm:text-xs text-[#5A8BA8] truncate">{t('msg.last_activity', { time: timeAgo(activeConv.last_message_at, t, loc) })}</p>
        </div>
      </div>

      {/* Listing card */}
      <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 border-b border-[#E8F4FD] bg-[#F0F7FA] shrink-0">
        {listingSnap.photo ? (
          <img src={photoAPI.getUrl(listingSnap.photo)} alt="" className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg object-cover shrink-0" />
        ) : (
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg bg-[#D6E8F0] shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[#033871] text-xs sm:text-sm truncate">{listingSnap.brand} {listingSnap.model}</p>
          <p className="font-bold text-[#0C63BF] text-sm sm:text-base">{listingSnap.price}&euro;</p>
        </div>
      </div>

      {/* Seller info card (compact) - hidden on mobile */}
      {otherUser && (
        <div className="hidden sm:block mx-3 my-2 p-3 border border-[#E8F4FD] rounded-xl bg-white shrink-0">
          <p className="font-semibold text-[#033871] text-sm mb-2">{isSeller ? t('msg.about_buyer') : t('msg.about_seller')}</p>
          <div className="space-y-1.5 text-xs text-[#5A8BA8]">
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-[#B8D4E3]" />
              <span>{t('msg.member_since', { date: memberSince(otherUser.created_at, loc) })}</span>
            </div>
            {otherUser.rating_count > 0 && (
              <div className="flex items-center gap-2">
                <Star className="w-3.5 h-3.5 text-[#B8D4E3]" />
                <span>{t('msg.ratings', { count: otherUser.rating_count })}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages area */}
      <div ref={messagesContainer} className="flex-1 overflow-y-auto px-2 sm:px-3 py-2 space-y-2 sm:space-y-3">
        {messages.length === 0 && (
          <p className="text-center text-xs sm:text-sm text-[#B8D4E3] py-6 sm:py-8">{t('msg.send_first')}</p>
        )}
        
        {groupedMessages.map((item, idx) => {
          if (item.type === 'date') {
            return (
              <div key={`date-${idx}`} className="text-center py-1 sm:py-2">
                <span className="text-[10px] sm:text-xs text-[#B8D4E3]">{formatDate(item.date, loc)}</span>
              </div>
            );
          }
          
          const m = item;
          const isMine = m.sender_id === user?.id;
          const isOfferMessage = m.is_offer_message || /^[^\w\s]/.test(m.content?.charAt(0) || '');
          
          // Offer message card (PadSmash style)
          if (isOfferMessage) {
            const isAccepted = m.content?.includes('acceptee') || m.content?.includes('Acceptee');
            const isDeclined = m.content?.includes('refusee') || m.content?.includes('Refusee');
            const isCounter = m.content?.includes('Contre-offre');
            
            return (
              <div key={m.id} className="flex justify-start" data-testid={`message-${m.id}`}>
                <div className="w-full max-w-[280px] sm:max-w-sm">
                  <div className="flex items-start gap-1.5 sm:gap-2">
                    {/* Icon */}
                    <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shrink-0 ${
                      isAccepted ? 'bg-[#32CD32]/20' : isDeclined ? 'bg-red-100' : isCounter ? 'bg-[#E8F4FD]' : 'bg-[#E8F4FD]'
                    }`}>
                      {isAccepted ? <Check className="w-3 h-3 sm:w-4 sm:h-4 text-[#32CD32]" /> :
                       isDeclined ? <X className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" /> :
                       isCounter ? <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 text-[#0C63BF]" /> :
                       <Euro className="w-3 h-3 sm:w-4 sm:h-4 text-[#0C63BF]" />}
                    </div>
                    
                    {/* Card */}
                    <div className="flex-1 border border-[#E8F4FD] rounded-xl p-2 sm:p-3 bg-[#F0F7FA]">
                      <p className="font-semibold text-[#033871] text-xs sm:text-sm mb-0.5 sm:mb-1">
                        {isAccepted ? t('msg.card_accepted') : 
                         isDeclined ? t('msg.card_declined') :
                         isCounter ? t('msg.card_counter') :
                         isMine ? t('msg.card_sent') : t('msg.card_received')}
                      </p>
                      <p className="text-xs sm:text-sm text-[#5A8BA8]">{m.content?.replace(/^[^\w\s]+\s*/, '')}</p>
                      <p className="text-[9px] sm:text-[10px] text-[#B8D4E3] mt-1 sm:mt-2">{t('msg.auto_message')}</p>
                    </div>
                  </div>
                  <p className="text-[9px] sm:text-[10px] text-[#B8D4E3] mt-1 text-right">{timeAgo(m.created_at, t, loc)}</p>
                </div>
              </div>
            );
          }
          
          // Regular message
          return (
            <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[80%] sm:max-w-[75%]">
                <div className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl text-xs sm:text-sm ${
                  isMine 
                    ? 'bg-[#0C63BF] text-white rounded-br-md' 
                    : 'bg-[#F0F7FA] text-[#033871] rounded-bl-md'
                }`} data-testid={`message-${m.id}`}>
                  <p className="whitespace-pre-wrap break-words">{m.translated_content && !showOriginal[m.id] ? m.translated_content : m.content}</p>
                </div>
                {m.translated_content && (
                  <button
                    onClick={() => setShowOriginal(p => ({ ...p, [m.id]: !p[m.id] }))}
                    className={`flex items-center gap-1 text-[9px] sm:text-[10px] text-[#5A8BA8] hover:text-[#0C63BF] mt-0.5 ${isMine ? 'ml-auto' : ''}`}
                    data-testid={`toggle-translation-${m.id}`}
                  >
                    <Languages className="w-2.5 h-2.5" />
                    {showOriginal[m.id] ? t('msg.show_translation') : `${t('msg.translated_badge')} · ${t('msg.show_original')}`}
                  </button>
                )}
                <p className={`text-[9px] sm:text-[10px] text-[#B8D4E3] mt-0.5 ${isMine ? 'text-right' : 'text-left'}`}>
                  {timeAgo(m.created_at, t, loc)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEnd} />
      </div>

      {/* Offer action buttons */}
      {showOfferActions && (
        <div className="px-2 sm:px-3 py-2 border-t border-[#E8F4FD] bg-white shrink-0">
          {showCounter ? (
            <div className="flex gap-1.5 sm:gap-2 items-center">
              <input 
                type="number"
                value={counterAmount}
                onChange={e => setCounterAmount(e.target.value)}
                placeholder={`${activeOffer.amount + 1} - ${activeOffer.original_price - 1}€`}
                className="flex-1 h-9 sm:h-10 px-2 sm:px-3 rounded-lg border border-[#E8F4FD] text-xs sm:text-sm focus:border-[#0C63BF] outline-none"
              />
              <Button onClick={handleCounterOffer} disabled={offerLoading}
                className="h-9 sm:h-10 px-3 sm:px-4 bg-[#0C63BF] hover:bg-[#0952a0] text-white rounded-lg text-xs sm:text-sm">
                OK
              </Button>
              <Button onClick={() => setShowCounter(false)} variant="ghost" className="h-9 sm:h-10 px-2 sm:px-3 text-[#5A8BA8] text-xs sm:text-sm">
                X
              </Button>
            </div>
          ) : (
            <div className="flex gap-1.5 sm:gap-2">
              <Button onClick={handleDeclineOffer} disabled={offerLoading}
                variant="outline"
                className="flex-1 h-9 sm:h-11 border-[#5A8BA8] text-[#5A8BA8] hover:bg-[#F0F7FA] rounded-lg font-medium text-xs sm:text-sm px-2 sm:px-4"
                data-testid="decline-offer-btn">
                {t('msg.decline')}
              </Button>
              {isSeller && activeOffer.status === 'pending' && (
                <Button onClick={() => setShowCounter(true)} disabled={offerLoading}
                  variant="outline"
                  className="h-9 sm:h-11 px-2 sm:px-4 border-[#0C63BF] text-[#0C63BF] hover:bg-[#E8F4FD] rounded-lg font-medium text-xs sm:text-sm">
                  {t('msg.negotiate')}
                </Button>
              )}
              <Button onClick={handleAcceptOffer} disabled={offerLoading}
                className="flex-1 h-9 sm:h-11 bg-[#32CD32] hover:bg-[#28a428] text-white rounded-lg font-medium text-xs sm:text-sm px-2 sm:px-4"
                data-testid="accept-offer-btn">
                {t('msg.accept')} {offerDisplayAmount}&euro;
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Make offer button for buyer */}
      {canMakeOffer && (
        <div className="px-2 sm:px-3 py-2 border-t border-[#E8F4FD] bg-white shrink-0">
          {showMakeOffer ? (
            <div className="flex gap-1.5 sm:gap-2 items-center">
              <input 
                type="number"
                value={offerAmount}
                onChange={e => setOfferAmount(e.target.value)}
                placeholder={`< ${listingSnap.price}€`}
                className="flex-1 h-9 sm:h-10 px-2 sm:px-3 rounded-lg border border-[#E8F4FD] text-xs sm:text-sm focus:border-[#0C63BF] outline-none"
              />
              <Button onClick={handleMakeOffer} disabled={offerLoading}
                className="h-9 sm:h-10 px-3 sm:px-4 bg-[#0C63BF] hover:bg-[#0952a0] text-white rounded-lg text-xs sm:text-sm">
                OK
              </Button>
              <Button onClick={() => setShowMakeOffer(false)} variant="ghost" className="h-9 sm:h-10 px-2 sm:px-3 text-[#5A8BA8] text-xs sm:text-sm">
                X
              </Button>
            </div>
          ) : (
            <div className="flex gap-1.5 sm:gap-2">
              <Button onClick={() => setShowMakeOffer(true)}
                variant="outline"
                className="flex-1 h-9 sm:h-11 border-[#0C63BF] text-[#0C63BF] hover:bg-[#E8F4FD] rounded-lg font-medium text-xs sm:text-sm"
                data-testid="make-offer-btn">
                {t('msg.make_offer')}
              </Button>
              <Button 
                onClick={() => window.location.href = `/annonce/${activeConv.listing_id}`}
                className="flex-1 h-9 sm:h-11 bg-[#32CD32] hover:bg-[#28a428] text-white rounded-lg font-medium text-xs sm:text-sm"
                data-testid="buy-now-btn">
                {t('msg.buy')} {listingSnap.price}&euro;
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Message input */}
      <div className="p-2 sm:p-3 border-t border-[#E8F4FD] flex gap-1.5 sm:gap-2 items-center bg-white shrink-0">
        <input value={newMsg} onChange={e => setNewMsg(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
          placeholder={t('msg.write_message')}
          className="flex-1 h-9 sm:h-10 px-3 sm:px-4 rounded-full border border-[#E8F4FD] text-xs sm:text-sm focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none"
          data-testid="message-input" />
        <Button onClick={handleSend} disabled={!newMsg.trim() || sending}
          className="h-9 w-9 sm:h-10 sm:w-10 p-0 bg-[#0C63BF] hover:bg-[#0952a0] text-white rounded-full shrink-0" 
          data-testid="send-message-btn">
          <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </div>
  );
}
